/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { Plus, ArrowDown, ArrowUp, Trash2, History, X, Pencil, Save } from "lucide-react";
import { fmtDateShortIST, fmtDateTimeIST } from "../lib/formatDateTime";

const UNITS = ["pcs", "box", "kg", "g", "ltr", "ml", "roll", "pack", "set"];

export default function StocksPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [products, setProducts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", unit: "pcs", opening_qty: 0, category: "", notes: "" });
  const [moveFor, setMoveFor] = useState(null); // product object being moved
  const [moveForm, setMoveForm] = useState({ direction: "in", qty: "", note: "" });
  const [historyFor, setHistoryFor] = useState(null);
  const [editFor, setEditFor] = useState(null); // product being edited
  const [editForm, setEditForm] = useState({ name: "", unit: "pcs", opening_qty: 0, category: "", notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    if (!DEMO) return;
    const { data } = await api.get(`/matches/${DEMO}/stocks`);
    setProducts(data);
  };
  useEffect(() => { load(); }, [DEMO]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/matches/${DEMO}/stocks`, { ...form, opening_qty: Number(form.opening_qty) || 0 });
      setForm({ name: "", unit: "pcs", opening_qty: 0, category: "", notes: "" });
      setShowNew(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add product");
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove product "${p.name}" and all its movements?`)) return;
    await api.delete(`/stocks/${p.id}`);
    load();
  };

  const openEdit = (p) => {
    setEditFor(p);
    setEditForm({
      name: p.name || "",
      unit: p.unit || "pcs",
      opening_qty: p.opening_qty ?? 0,
      category: p.category || "",
      notes: p.notes || "",
    });
    setError("");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.patch(`/stocks/${editFor.id}`, {
        name: editForm.name,
        unit: editForm.unit,
        opening_qty: Number(editForm.opening_qty) || 0,
        category: editForm.category,
        notes: editForm.notes,
      });
      setEditFor(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update product");
    }
  };

  const openMove = (p, direction) => {
    setMoveFor(p);
    setMoveForm({ direction, qty: "", note: "" });
    setError("");
  };

  const submitMove = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/stocks/${moveFor.id}/movements`, {
        direction: moveForm.direction,
        qty: Number(moveForm.qty),
        note: moveForm.note,
      });
      setMoveFor(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to record movement");
    }
  };

  const deleteMovement = async (productId, movementId) => {
    if (!window.confirm("Delete this movement entry?")) return;
    await api.delete(`/stocks/${productId}/movements/${movementId}`);
    load();
    if (historyFor?.id === productId) {
      const updated = (await api.get(`/matches/${DEMO}/stocks`)).data.find((x) => x.id === productId);
      setHistoryFor(updated || null);
    }
  };

  if (!selectedMatchId) return <RequireMatch />;

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-stocks">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Pre-Match · Inventory</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Stocks</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">Track every consumable and asset for this match. Add a product, log inward receipts and outward issues, and monitor the live balance.</p>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs"
          data-testid="stock-new-btn"
        >
          {showNew ? "Cancel" : "+ Add Product"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="stock-form">
          <input
            className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2"
            placeholder="Product name (e.g., Cable ties)"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="stock-form-name"
          />
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            data-testid="stock-form-unit"
          >
            {UNITS.map((u) => <option key={u}>{u}</option>)}
          </select>
          <input
            type="number"
            step="any"
            min="0"
            className="bg-black border border-cmdBorder p-2 text-sm"
            placeholder="Opening qty"
            value={form.opening_qty}
            onChange={(e) => setForm({ ...form, opening_qty: e.target.value })}
            data-testid="stock-form-opening"
          />
          <input
            className="bg-black border border-cmdBorder p-2 text-sm"
            placeholder="Category (optional)"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <button type="submit" className="bg-readyGreen text-black font-bold uppercase tracking-widest text-xs" data-testid="stock-form-submit">Add</button>
          <input
            className="bg-black border border-cmdBorder p-2 text-sm md:col-span-6"
            placeholder="Notes (supplier, min-level, storage…)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </form>
      )}

      {error && <div className="mb-4 text-incidentRed text-sm font-mono-live" data-testid="stock-error">{error}</div>}

      <div className="mc-panel overflow-x-auto hidden md:block">
        <table className="w-full mc-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th className="text-right">Opening</th>
              <th className="text-right">IN</th>
              <th className="text-right">OUT</th>
              <th className="text-right">Balance</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Group by category (empty → "Uncategorised"), alphabetically ordered.
              const groups = {};
              for (const p of products) {
                const key = (p.category || "").trim() || "Uncategorised";
                if (!groups[key]) groups[key] = [];
                groups[key].push(p);
              }
              const orderedCats = Object.keys(groups).sort((a, b) => {
                // Push "Uncategorised" to the bottom, otherwise pure alpha.
                if (a === "Uncategorised") return 1;
                if (b === "Uncategorised") return -1;
                return a.localeCompare(b);
              });
              if (orderedCats.length === 0) {
                return (
                  <tr><td colSpan={7} className="text-center text-zinc-500 py-8">No products yet. Click "+ Add Product" to start.</td></tr>
                );
              }
              return orderedCats.flatMap((cat) => {
                const rowsInGroup = groups[cat].slice().sort((a, b) => a.name.localeCompare(b.name));
                const header = (
                  <tr key={`cat-${cat}`} data-testid={`stock-cat-${cat}`} className="bg-white/[0.03]">
                    <td colSpan={7} className="py-2">
                      <div className="flex items-center gap-2">
                        <span className="mc-label">{cat}</span>
                        <span className="font-mono-live text-[10px] text-zinc-500">({rowsInGroup.length})</span>
                      </div>
                    </td>
                  </tr>
                );
                const rowEls = rowsInGroup.map((p) => {
                  const low = (p.balance ?? 0) === 0;
                  return (
                    <tr key={p.id} data-testid={`stock-row-${p.id}`}>
                      <td>
                        <div className="font-medium">{p.name} <span className="text-zinc-500 text-xs">/ {p.unit}</span></div>
                        {p.notes && <div className="text-xs text-zinc-500 mt-0.5">{p.notes}</div>}
                      </td>
                      <td className="text-zinc-300">{p.category || <span className="text-zinc-600">—</span>}</td>
                      <td className="text-right font-mono-live text-zinc-400">{p.opening_qty}</td>
                      <td className="text-right font-mono-live text-readyGreen" data-testid={`stock-in-${p.id}`}>+{p.in_total}</td>
                      <td className="text-right font-mono-live text-incidentRed" data-testid={`stock-out-${p.id}`}>-{p.out_total}</td>
                      <td className="text-right font-mono-live font-bold" style={{ color: low ? "#DC2626" : "#0F172A" }} data-testid={`stock-balance-${p.id}`}>
                        {p.balance}
                      </td>
                      <td className="text-right">
                        <div className="inline-flex gap-2 justify-end">
                          <button
                            onClick={() => openMove(p, "in")}
                            className="flex items-center gap-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`stock-in-btn-${p.id}`}
                            title="Record stock inward"
                          >
                            <ArrowDown className="w-3 h-3" /> IN
                          </button>
                          <button
                            onClick={() => openMove(p, "out")}
                            className="flex items-center gap-1 border border-incidentRed text-incidentRed hover:bg-incidentRed hover:text-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`stock-out-btn-${p.id}`}
                            title="Record stock outward"
                          >
                            <ArrowUp className="w-3 h-3" /> OUT
                          </button>
                          <button
                            onClick={() => setHistoryFor(p)}
                            className="flex items-center gap-1 border border-cmdBorder hover:border-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`stock-history-${p.id}`}
                            title="View movement history"
                          >
                            <History className="w-3 h-3" /> {p.movements?.length || 0}
                          </button>
                          <button
                            onClick={() => openEdit(p)}
                            className="border border-cmdBorder hover:border-white px-2 py-1 text-xs transition-colors"
                            data-testid={`stock-edit-${p.id}`}
                            title="Edit product"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => deleteProduct(p)}
                            className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs transition-colors"
                            data-testid={`stock-del-${p.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                });
                return [header, ...rowEls];
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* Mobile: grouped card list — replaces the wide table on small screens */}
      <div className="md:hidden space-y-4" data-testid="stocks-mobile-list">
        {(() => {
          const groups = {};
          for (const p of products) {
            const key = (p.category || "").trim() || "Uncategorised";
            if (!groups[key]) groups[key] = [];
            groups[key].push(p);
          }
          const orderedCats = Object.keys(groups).sort((a, b) => {
            if (a === "Uncategorised") return 1;
            if (b === "Uncategorised") return -1;
            return a.localeCompare(b);
          });
          if (orderedCats.length === 0) {
            return <div className="mc-panel p-6 text-center text-zinc-500 text-sm">No products yet. Tap "+ Add Product" to start.</div>;
          }
          return orderedCats.map((cat) => {
            const items = groups[cat].slice().sort((a, b) => a.name.localeCompare(b.name));
            return (
              <div key={cat} className="mc-panel" data-testid={`stock-cat-m-${cat}`}>
                <div className="px-4 py-2 bg-white/[0.03] border-b border-cmdBorder flex items-center gap-2">
                  <span className="mc-label">{cat}</span>
                  <span className="font-mono-live text-[10px] text-zinc-500">({items.length})</span>
                </div>
                <div className="divide-y divide-cmdBorder">
                  {items.map((p) => {
                    const low = (p.balance ?? 0) === 0;
                    return (
                      <div key={p.id} className="p-4" data-testid={`stock-card-${p.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{p.name} <span className="text-zinc-500 text-xs">/ {p.unit}</span></div>
                            {p.notes && <div className="text-xs text-zinc-500 mt-0.5">{p.notes}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Balance</div>
                            <div className="font-mono-live font-bold text-2xl" style={{ color: low ? "#DC2626" : undefined }} data-testid={`stock-balance-m-${p.id}`}>
                              {p.balance}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                          <div className="border border-cmdBorder p-2 text-center">
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Opening</div>
                            <div className="font-mono-live text-zinc-300 mt-0.5">{p.opening_qty}</div>
                          </div>
                          <div className="border border-cmdBorder p-2 text-center">
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500">IN</div>
                            <div className="font-mono-live text-readyGreen mt-0.5" data-testid={`stock-in-m-${p.id}`}>+{p.in_total}</div>
                          </div>
                          <div className="border border-cmdBorder p-2 text-center">
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500">OUT</div>
                            <div className="font-mono-live text-incidentRed mt-0.5" data-testid={`stock-out-m-${p.id}`}>-{p.out_total}</div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => openMove(p, "in")}
                            className="flex-1 flex items-center justify-center gap-1 border border-readyGreen text-readyGreen h-10 text-xs uppercase tracking-widest"
                            data-testid={`stock-in-btn-m-${p.id}`}
                          >
                            <ArrowDown className="w-4 h-4" /> IN
                          </button>
                          <button
                            onClick={() => openMove(p, "out")}
                            className="flex-1 flex items-center justify-center gap-1 border border-incidentRed text-incidentRed h-10 text-xs uppercase tracking-widest"
                            data-testid={`stock-out-btn-m-${p.id}`}
                          >
                            <ArrowUp className="w-4 h-4" /> OUT
                          </button>
                          <button
                            onClick={() => setHistoryFor(p)}
                            className="border border-cmdBorder hover:border-white h-10 min-w-[44px] px-2 flex items-center justify-center gap-1 text-xs"
                            data-testid={`stock-history-m-${p.id}`}
                            aria-label="Movement history"
                          >
                            <History className="w-4 h-4" /> {p.movements?.length || 0}
                          </button>
                          <button
                            onClick={() => openEdit(p)}
                            className="border border-cmdBorder hover:border-white h-10 w-10 flex items-center justify-center"
                            data-testid={`stock-edit-m-${p.id}`}
                            aria-label="Edit product"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteProduct(p)}
                            className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed h-10 w-10 flex items-center justify-center"
                            data-testid={`stock-del-m-${p.id}`}
                            aria-label="Delete product"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Edit product modal */}
      {editFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditFor(null)} data-testid="stock-edit-modal">
          <form
            onSubmit={submitEdit}
            className="mc-panel p-5 max-w-lg w-full space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="mc-label">Edit Product</div>
                <h3 className="font-head font-bold text-2xl uppercase mt-1">{editFor.name}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  Movements stay untouched. Balance recomputes from the new opening.
                </div>
              </div>
              <button type="button" onClick={() => setEditFor(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="mc-label">Product name</label>
                <input
                  required
                  className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="stock-edit-name"
                />
              </div>
              <div>
                <label className="mc-label">Unit</label>
                <select
                  className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.unit}
                  onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                  data-testid="stock-edit-unit"
                >
                  {UNITS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="mc-label">Opening quantity</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live"
                  value={editForm.opening_qty}
                  onChange={(e) => setEditForm({ ...editForm, opening_qty: e.target.value })}
                  data-testid="stock-edit-opening"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mc-label">Category</label>
                <input
                  className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  data-testid="stock-edit-category"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mc-label">Notes</label>
                <input
                  className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  data-testid="stock-edit-notes"
                />
              </div>
            </div>
            {error && <div className="text-incidentRed text-sm font-mono-live" data-testid="stock-edit-error">{error}</div>}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className="flex-1 flex items-center justify-center gap-1 bg-readyGreen text-white py-2 font-bold uppercase tracking-widest text-xs hover:brightness-110"
                data-testid="stock-edit-submit"
              >
                <Save className="w-3 h-3" /> Save Changes
              </button>
              <button type="button" onClick={() => setEditFor(null)} className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Movement modal */}
      {moveFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setMoveFor(null)} data-testid="stock-move-modal">
          <form
            onSubmit={submitMove}
            className="mc-panel p-5 max-w-md w-full space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="mc-label">Record Movement</div>
                <h3 className="font-head font-bold text-2xl uppercase mt-1">{moveForm.direction === "in" ? "Stock Inward" : "Stock Outward"}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  {moveFor.name} · Current balance: <span className="text-white font-mono-live">{moveFor.balance} {moveFor.unit}</span>
                </div>
              </div>
              <button type="button" onClick={() => setMoveFor(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="mc-label">Quantity</label>
              <input
                type="number"
                step="any"
                min="0.0001"
                required
                autoFocus
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live"
                placeholder={`e.g., 20 (in ${moveFor.unit})`}
                value={moveForm.qty}
                onChange={(e) => setMoveForm({ ...moveForm, qty: e.target.value })}
                data-testid="stock-move-qty"
              />
            </div>
            <div>
              <label className="mc-label">Note (optional)</label>
              <input
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                placeholder={moveForm.direction === "in" ? "Supplier / GRN No." : "Issued to / purpose"}
                value={moveForm.note}
                onChange={(e) => setMoveForm({ ...moveForm, note: e.target.value })}
                data-testid="stock-move-note"
              />
            </div>
            {error && <div className="text-incidentRed text-sm font-mono-live" data-testid="stock-move-error">{error}</div>}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className={`flex-1 py-2 font-bold uppercase tracking-widest text-xs ${moveForm.direction === "in" ? "bg-readyGreen text-black" : "bg-incidentRed text-white"}`}
                data-testid="stock-move-submit"
              >
                Record {moveForm.direction === "in" ? "Inward" : "Outward"}
              </button>
              <button type="button" onClick={() => setMoveFor(null)} className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* History drawer */}
      {historyFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setHistoryFor(null)} data-testid="stock-history-modal">
          <div className="mc-panel p-5 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-cmdBorder pb-3 mb-3">
              <div>
                <div className="mc-label">Movement History</div>
                <h3 className="font-head font-bold text-2xl uppercase mt-1">{historyFor.name}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  Opening {historyFor.opening_qty} · IN +{historyFor.in_total} · OUT -{historyFor.out_total} · Balance <span className="text-white font-mono-live">{historyFor.balance} {historyFor.unit}</span>
                </div>
              </div>
              <button onClick={() => setHistoryFor(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {(historyFor.movements?.length || 0) === 0 && (
              <div className="text-center text-zinc-500 py-8">No movements yet.</div>
            )}
            <div className="space-y-2">
              {(historyFor.movements || []).slice().reverse().map((m) => (
                <div key={m.id} className="flex items-center justify-between border border-cmdBorder p-3" data-testid={`stock-move-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${m.direction === "in" ? "bg-readyGreen text-black" : "bg-incidentRed text-white"}`}>
                      {m.direction === "in" ? "IN" : "OUT"}
                    </span>
                    <span className="font-mono-live font-bold text-lg">
                      {m.direction === "in" ? "+" : "-"}{m.qty} <span className="text-zinc-500 text-xs">{historyFor.unit}</span>
                    </span>
                    {m.note && <span className="text-xs text-zinc-400">— {m.note}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-zinc-500 text-right">
                      <div>{fmtDateTimeIST(m.at)}</div>
                      <div className="text-zinc-600">{m.by}</div>
                    </div>
                    <button
                      onClick={() => deleteMovement(historyFor.id, m.id)}
                      className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs"
                      data-testid={`stock-del-move-${m.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
